import React from 'react'

import { useClass } from '@/hooks/useClass'

import { IconClose } from '../../General'

type TextFieldProps = React.ComponentProps<'input'> & {
  label?: string
  wrapperProps?: React.ComponentProps<'div'>
  error?: string
  description?: string
  wordCount?: {
    max: number
    current: number
  }
  isShowingDeleteButton?: boolean
  onClickDelete?: () => void
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      wrapperProps,
      className,
      error,
      description,
      wordCount,
      required,
      isShowingDeleteButton,
      onClickDelete,
      ...props
    },
    ref
  ) => {
    const uniqueId = React.useId()

    const { className: wrapperClassName, ...restWrapperProps } =
      wrapperProps || {}

    const { max = 0, current = 0 } = wordCount || {}

    const isLimitOver = max < current

    const isError = Boolean(error) || isLimitOver

    const wrapperClass = useClass('font-bold flex flex-col', wrapperClassName)

    const inputClass = useClass(
      'border p-16 bg-transparent rounded-lg caret-interactive-from font-bold w-full',
      className,
      isError ? 'border-error bump-shadow-error' : 'border-ui-2'
    )

    const textLengthClass = useClass(
      'text-xs',
      isLimitOver ? 'text-error' : 'text-secondary'
    )

    const currentLengthClass = useClass(
      'text-xs',
      isLimitOver ? 'text-error' : 'text-primary'
    )

    return (
      <div className={wrapperClass} {...restWrapperProps}>
        <div className="flex justify-between">
          <div className="pb-8">
            {label && <label htmlFor={uniqueId}>{label}</label>}
            {required ? (
              <label className="ml-4 text-2xs text-primary-gradient">
                必須
              </label>
            ) : (
              <label className="ml-4 text-2xs font-bold text-secondary">
                任意
              </label>
            )}
          </div>
          {wordCount && (
            <span className={textLengthClass}>
              <span className={currentLengthClass}>{current}</span>/{max}
            </span>
          )}
        </div>
        <div className="flex relative items-center w-full">
          <input
            id={uniqueId}
            ref={ref}
            type="text"
            className={inputClass}
            {...props}
          />
          {isShowingDeleteButton && !!onClickDelete && (
            <button
              aria-label="入力された文字を削除"
              type="button"
              onClick={onClickDelete}
              className="absolute right-16 text-md"
            >
              <IconClose className="fill-primary" />
            </button>
          )}
        </div>
        {description && (
          <p className=" pt-8 text-xs text-secondary">{description}</p>
        )}
        {Boolean(error) && (
          <p className="pt-4 font-normal text-error">{error}</p>
        )}
      </div>
    )
  }
)
